import React, { useEffect, useState } from 'react';
import { Avatar, CircularProgress } from '@mui/material';
import publicIconService from '../services/publicIconService';

const PublicTokenIcon = ({ coinSymbol, size = 32 }) => {
  const [iconUrl, setIconUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  useEffect(() => {
    const loadIcon = async () => {
      if (!coinSymbol) return;
      
      // Reset state
      setIsLoading(false);
      setIsLoaded(false);
      setIsFailed(false);
      
      // Refresh coin data if needed
      await publicIconService.refreshCoinDataIfNeeded();
      
      // Get coin data to access all available URLs
      const coin = publicIconService.getCoinData(coinSymbol);
      if (!coin) {
        setIsFailed(true);
        return;
      }
      
      // Get all available URLs for this coin
      const urls = publicIconService.getAvailableIconUrls(coinSymbol);
      
      if (urls.length === 0) {
        setIsFailed(true);
        return;
      }
      
      // Try each URL in order until one works
      let foundWorkingUrl = false;
      for (let i = 0; i < urls.length && !foundWorkingUrl; i++) {
        const url = urls[i];
        
        // Check if already loaded
        if (publicIconService.isIconLoaded(url)) {
          setIconUrl(url);
          setIsLoaded(true);
          foundWorkingUrl = true;
          break;
        }
        
        // Check if currently loading
        if (publicIconService.isIconLoading(url)) {
          setIconUrl(url);
          setIsLoading(true);
          
          // Wait for loading to complete
          const checkLoading = () => {
            if (publicIconService.isIconLoaded(url)) {
              setIsLoading(false);
              setIsLoaded(true);
            } else if (publicIconService.isIconFailed(url)) {
              setIsLoading(false);
              // Continue to next URL by breaking out of the loop
              foundWorkingUrl = false;
              return;
            } else {
              setTimeout(checkLoading, 100);
            }
          };
          checkLoading();
          return;
        }
        
        // Check if failed - skip this URL
        if (publicIconService.isIconFailed(url)) {
          continue;
        }
        
        // Try to load this URL
        setIconUrl(url);
        setIsLoading(true);
        
        const success = await publicIconService.loadIcon(url);
        
        if (success) {
          setIsLoading(false);
          setIsLoaded(true);
          foundWorkingUrl = true;
          break;
        } else {
          setIsLoading(false);
          // Continue to next URL
        }
      }
      
      // If we get here, all URLs failed
      if (!foundWorkingUrl) {
        setIsFailed(true);
      }
    };
    
    loadIcon();
  }, [coinSymbol]);

  if (iconUrl && isLoaded) {
    return (
      <Avatar 
        src={iconUrl} 
        alt={coinSymbol} 
        sx={{ 
          width: size, 
          height: size
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <Avatar sx={{ 
        width: size, 
        height: size, 
        bgcolor: 'grey.300',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size={size * 0.5} />
      </Avatar>
    );
  }
  
  // Fallback to coin symbol
  return (
    <Avatar sx={{ 
      width: size, 
      height: size, 
      bgcolor: 'primary.main',
      fontSize: `${size * 0.4}px` // Scale font size with avatar size
    }}>
      {(coinSymbol || '??').substring(0, 2).toUpperCase()}
    </Avatar>
  );
};

export default PublicTokenIcon; 